<script lang="ts" setup>
/**
 * 投稿者プロフィール投稿フォーム用。
 */
import { inject } from "vue";
import { ApiValidationErrorViewModel } from "@/viewModels/apiErrorViewModel";
import { CommunityTalkStore, isValidCommunityTalkStoreKey } from "@/store/communityTalk/communityTalkStore";
import CustomLabel from "./CustomLabel.vue";
import PostReviewerAttributeItem from "./PostReviewerAttributeItem.vue";

interface Props {
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: string;
	/**
	 * 入力欄を無効化するかどうか。
	 */
	disabled?: boolean;
	/**
	 * 親コンポーネントからのAPIエラーオブジェクト。
	 */
	apiValidationErrorViewModel: ApiValidationErrorViewModel | null;
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-undef
const props = defineProps<Props>();

// Storeの初期化処理
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}`);
}
const communityTalkStore = inject(props.communityTalkStoreKey) as CommunityTalkStore;

/**
 * 投稿者プロフィール選択時の処理。
 */
const checkReviewerAttrChoice = (v: { reviewerAttributeId: string; attributeChoiceId: string; checked: boolean }) => {
	// Storeから更新前の投稿者プロフィール情報を取得
	let reviewerAttributeAnswers = communityTalkStore.getCommunityTalkUserReviewerAttributes();

	// 他の選択肢を選択している場合、回答から削除
	reviewerAttributeAnswers = communityTalkStore.getCommunityTalkUserReviewerAttributes().filter((x) => x.ReviewerAttributeId !== v.reviewerAttributeId);

	// 回答に追加
	if (v.checked) {
		reviewerAttributeAnswers.push({
			ReviewerAttributeId: v.reviewerAttributeId,
			AttributeChoiceIds: [v.attributeChoiceId],
		});
	}
	// Storeへ投稿者プロフィール情報を反映
	communityTalkStore.setCommunityTalkUserReviewerAttributes(reviewerAttributeAnswers);
};

// inputタグのID値を分別化するための固有識別子ID
const uniqueKey = crypto.randomUUID();
</script>

<template>
	<div class="revico-community-talk-post--reviewer-attribute">
		<ul class="revico-community-talk-post--reviewer-attribute-item" v-for="(reviewerAttribute, index) in communityTalkStore.getPostableReviewerAttributes()" :key="index">
			<li>
				<custom-label :text="reviewerAttribute.displayName" :disabled="disabled" />
				<post-reviewer-attribute-item
					:reviewerAttribute="reviewerAttribute"
					:reviewer-attribute-preset="communityTalkStore.getCommunityTalkUserReviewerAttributes().filter((e) => e.ReviewerAttributeId === reviewerAttribute.reviewerAttributeId)[0]"
					:unique-key="uniqueKey"
					@click-reviewer-attributettribute-choice="checkReviewerAttrChoice"
				/>
			</li>
		</ul>
	</div>
</template>

<style lang="scss">
.revico-widget .revico-community-talk-post {
	&--reviewer-attribute {
		&-item {
			margin: 12px 0 0 0;
			&:first-child {
				margin: 16px 0 0 0;
			}
		}
	}
}
</style>
